import axios from 'axios';

// Use local sprite sheets instead of CDN URLs
const SPRITE_SHEETS = {
  'apple': '/assets/emoji/apple/sheets/64.png',
  'google': '/assets/emoji/google/sheets/64.png',
  'twitter': '/assets/emoji/twitter/sheets/64.png',
  'facebook': '/assets/emoji/facebook/sheets/64.png'
};

// Function to fetch emoji metadata
const fetchEmojiMetadata = async () => {
  try {
    // Use local emoji data instead of fetching from GitHub
    const response = await axios.get('/assets/emoji/emoji.json');
    return response.data;
  } catch (error) {
    console.error('Error fetching emoji metadata:', error);
    return [];
  }
};

// Cache for emoji metadata to avoid repeated fetches
let emojiMetadataCache = null;

// Function to fetch all emojis
export const fetchEmojis = async (theme = 'apple') => {
  try {
    // Use cache if available, otherwise fetch
    if (!emojiMetadataCache) {
      emojiMetadataCache = await fetchEmojiMetadata();
    }
    
    // Get the sprite sheet URL for the selected theme
    const spriteSheetUrl = SPRITE_SHEETS[theme] || SPRITE_SHEETS['apple'];
    
    // Transform the data to match our application's structure
    return emojiMetadataCache
      .map(emoji => {
        // Calculate the position in percentage for the sprite sheet
        // Assuming the sheet is 51x51 emojis (this may vary, adjust as needed)
        const posX = (emoji.sheet_x / 61) * 100;
        const posY = (emoji.sheet_y / 61) * 100;
        
        return {
          id: emoji.unified,
          name: emoji.name || emoji.short_name,
          short_name: emoji.short_name,
          category: emoji.category || 'Uncategorized',
          spriteUrl: spriteSheetUrl,
          posX: posX,
          posY: posY,
          sheet_x: emoji.sheet_x,
          sheet_y: emoji.sheet_y,
          keywords: [
            emoji.name,
            ...(emoji.short_names || []),
            ...(emoji.keywords || [])
          ].filter(Boolean)
        };
      });
  } catch (error) {
    console.error('Error processing emojis:', error);
    // Return an empty array in case of error
    return [];
  }
};

// Function to group emojis by category
export const groupEmojisByCategory = (emojis) => {
  const categories = {};
  
  // Define the order of categories
  const categoryOrder = [
    'Smileys & Emotion',
    'People & Body',
    'Animals & Nature',
    'Food & Drink',
    'Travel & Places',
    'Activities',
    'Objects',
    'Symbols',
    'Flags',
    'Uncategorized'
  ];
  
  // Initialize categories with empty arrays
  categoryOrder.forEach(category => {
    categories[category] = [];
  });
  
  // Group emojis by category
  emojis.forEach(emoji => {
    const category = emoji.category || 'Uncategorized';
    
    // Map emoji-data categories to our simplified categories
    let mappedCategory = 'Uncategorized';
    
    if (category.includes('Smileys') || category.includes('Emotion')) {
      mappedCategory = 'Smileys & Emotion';
    } else if (category.includes('People') || category.includes('Body')) {
      mappedCategory = 'People & Body';
    } else if (category.includes('Animals') || category.includes('Nature')) {
      mappedCategory = 'Animals & Nature';
    } else if (category.includes('Food') || category.includes('Drink')) {
      mappedCategory = 'Food & Drink';
    } else if (category.includes('Travel') || category.includes('Places')) {
      mappedCategory = 'Travel & Places';
    } else if (category.includes('Activities')) {
      mappedCategory = 'Activities';
    } else if (category.includes('Objects')) {
      mappedCategory = 'Objects';
    } else if (category.includes('Symbols')) {
      mappedCategory = 'Symbols';
    } else if (category.includes('Flags')) {
      mappedCategory = 'Flags';
    }
    
    if (categories[mappedCategory]) {
      categories[mappedCategory].push(emoji);
    } else {
      categories['Uncategorized'].push(emoji);
    }
  });
  
  // Remove empty categories
  const filteredCategories = {};
  categoryOrder.forEach(category => {
    if (categories[category].length > 0) {
      filteredCategories[category] = categories[category];
    }
  });
  
  return filteredCategories;
};

// Function to fetch emojis by theme
export const fetchEmojisByTheme = async (theme) => {
  const validThemes = ['apple', 'google', 'twitter', 'facebook'];
  
  if (!validThemes.includes(theme)) {
    console.warn(`Theme "${theme}" not supported, falling back to apple`);
    theme = 'apple';
  }
  
  return fetchEmojis(theme);
};

// Function to search emojis
export const searchEmojis = (emojis, searchTerm) => {
  if (!searchTerm) return emojis;
  
  const term = searchTerm.toLowerCase();
  return emojis.filter(emoji => 
    emoji.name.toLowerCase().includes(term) || 
    emoji.keywords.some(keyword => 
      keyword && keyword.toLowerCase().includes(term)
    )
  );
}; 